import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Divider, IconButton, Link as MuiLink, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  DeleteOutline as DeleteConversationIcon,
  NotificationsActiveOutlined as NotificationsOnIcon,
  NotificationsOffOutlined as NotificationsOffIcon,
  SettingsOutlined as SettingsIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import unifiedApi from '../../utils/unifiedApi';
import { useChat } from '../../contexts/ChatContext';
import {
  chatDayKey, formatChatDate, isSystemMessage, startsNewGroup,
} from '../../utils/chatFormat';
import ChatChannelAvatar from './ChatChannelAvatar';
import {
  channelAudience, isDirectChannel, isLeagueWideChannel, isSparesChannel, leagueWideCaption,
} from '../../utils/chatChannelTypes';
import ChatChannelSettingsDialog from './ChatChannelSettingsDialog';
import ChatChannelMembers from './ChatChannelMembers';
import ChatMessageItem from './ChatMessageItem';
import ChatComposer from './ChatComposer';
import { dragHasFiles, pickPhotoFile } from './chatPhotoFiles';

export { MAX_MESSAGE_LENGTH } from './ChatComposer';
const NEAR_BOTTOM_PX = 120;
const SEEN_DEBOUNCE_MS = 1500;
// Start fetching older messages a little before the top is actually reached.
const OLDER_ROOT_MARGIN = '200px 0px 0px 0px';
// Pause between automatic older-page loads so the observer can catch up with the preserved scroll position.
export const OLDER_LOAD_COOLDOWN_MS = 300;

const DELETED_FIELDS = {
  isDeleted: true, content: '', attachment: null, gif: null, reactions: [],
};

/**
 * Applies an edit (hub event or save response) to the loaded messages. Only the edited fields are
 * taken from it: reactions/attachments keep their newer local state, a message deleted meanwhile
 * stays deleted, and an edit older than the one already shown is ignored.
 */
export const applyMessageEdit = (messages, edited) => messages.map((m) => {
  if (m.id !== edited.id || m.isDeleted) return m;
  if (edited.isDeleted) return { ...m, ...DELETED_FIELDS };
  if (m.editedAt && edited.editedAt && new Date(edited.editedAt) < new Date(m.editedAt)) return m;
  return {
    ...m, content: edited.content, editedAt: edited.editedAt, isEdited: edited.isEdited ?? Boolean(edited.editedAt),
  };
});

const upsertMessage = (messages, message) => {
  if (messages.some((m) => m.id === message.id)) return messages;
  return [...messages, message].sort((a, b) => a.id - b.id);
};

const ChatConversation = ({
  channel, isDesktop, showBack, onBack, autoFocusComposer = false,
}) => {
  const {
    userTenantId, subscribe, setActiveChannelId, markChannelSeen, connectionState, setChannelMuted, refreshChannels,
    openDirectChat, hideChannel,
  } = useChat();
  const navigate = useNavigate();
  const [muteBusy, setMuteBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hideOpen, setHideOpen] = useState(false);
  const [hiding, setHiding] = useState(false);
  const isLeague = isLeagueWideChannel(channel);
  const isDirect = isDirectChannel(channel);
  // Team channels link to the team, league-wide channels to the league (spares chats to its spares page),
  // direct chats to the other person.
  let titleLink = channel.teamSlug && `/teams/${channel.teamSlug}`;
  if (isLeague) titleLink = channel.leagueSlug && `/leagues/${channel.leagueSlug}${isSparesChannel(channel) ? '/spares' : ''}`;
  if (isDirect) titleLink = channel.directMember && `/player/${channel.directMember.userTenantId}`;

  const messageSender = async (message) => {
    try {
      const direct = await openDirectChat(message.senderUserTenantId);
      if (direct) navigate(`/chat/${direct.id}?compose=1`);
      else setError('You can\'t message this member.');
    } catch (err) {
      setError(err.message || 'Failed to open the conversation.');
    }
  };

  const toggleMute = async () => {
    setMuteBusy(true);
    try {
      await setChannelMuted(channel.id, !channel.isMuted);
    } catch (err) {
      setError(err.message || 'Failed to update notifications for this chat.');
    } finally {
      setMuteBusy(false);
    }
  };
  const confirmHide = async () => {
    setHiding(true);
    try {
      await hideChannel(channel.id);
      navigate('/chat', { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to delete the conversation.');
      setHiding(false);
      setHideOpen(false);
    }
  };
  const channelId = channel.id;

  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderLoadFailed, setOlderLoadFailed] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const scrollRef = useRef(null);
  const scrollModeRef = useRef(null);
  const seenTimerRef = useRef(null);
  const messagesRef = useRef([]);
  const composerRef = useRef(null);
  const sentinelRef = useRef(null);
  const dragDepthRef = useRef(0);

  // Infinite scroll state lives in refs so observer callbacks never act on stale values or double-fire.
  const hasMoreRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const olderLoadFailedRef = useRef(false);
  const autoLoadArmedRef = useRef(false);
  const sentinelVisibleRef = useRef(false);
  const cooldownUntilRef = useRef(0);
  const autoLoadTimerRef = useRef(null);
  const pageGenerationRef = useRef(0);

  const supportsObserver = typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined';

  // True until the latest page has been loaded (initially and during a resync).
  const latestPendingRef = useRef(true);

  const updateHasMore = useCallback((value) => {
    hasMoreRef.current = Boolean(value);
    setHasMore(Boolean(value));
  }, []);

  const setOlderFailed = useCallback((value) => {
    olderLoadFailedRef.current = value;
    setOlderLoadFailed(value);
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
  };

  const newestMessageId = () => {
    const list = messagesRef.current;
    return list.length ? list[list.length - 1].id : undefined;
  };

  const scheduleSeen = useCallback(() => {
    clearTimeout(seenTimerRef.current);
    seenTimerRef.current = setTimeout(() => markChannelSeen(channelId, newestMessageId()), SEEN_DEBOUNCE_MS);
  }, [channelId, markChannelSeen]);

  const loadOlderRef = useRef(null);
  const maybeAutoLoadRef = useRef(null);

  const scheduleAutoLoadCheck = useCallback((delay) => {
    clearTimeout(autoLoadTimerRef.current);
    autoLoadTimerRef.current = setTimeout(() => maybeAutoLoadRef.current?.(), delay);
  }, []);

  /**
   * Allows automatic older-page loads once the initial (or resynced) page has been scrolled into place.
   * A short cooldown lets the observer report where the sentinel ended up before anything is fetched.
   */
  const armAutoLoad = useCallback(() => {
    if (autoLoadArmedRef.current || latestPendingRef.current) return;
    autoLoadArmedRef.current = true;
    cooldownUntilRef.current = Date.now() + OLDER_LOAD_COOLDOWN_MS;
    scheduleAutoLoadCheck(OLDER_LOAD_COOLDOWN_MS);
  }, [scheduleAutoLoadCheck]);

  const disarmAutoLoad = useCallback(() => {
    latestPendingRef.current = true;
    autoLoadArmedRef.current = false;
    clearTimeout(autoLoadTimerRef.current);
  }, []);

  /**
   * Loads the latest page. On resync (after reconnecting / returning to the app) the view is reset
   * to that page: previously loaded older pages may have gaps or stale edits/deletions, so they're
   * dropped and are re-fetched by scrolling up (or "Load earlier messages").
   */
  const loadLatest = useCallback(async ({ resync = false } = {}) => {
    try {
      unifiedApi.clearCacheFor(`/api/chat/channels/${channelId}/messages`);
      const page = await unifiedApi.getChatMessages(channelId);
      if (!page) {
        setUnavailable(true);
        return;
      }
      const followBottom = !resync || isNearBottom();
      const fetched = page.messages || [];
      const newestFetchedId = fetched[fetched.length - 1]?.id ?? 0;
      updateHasMore(page.hasMore);
      if (resync) setOlderFailed(false);
      // Keep only messages broadcast while the request was in flight.
      setMessages((prev) => [...fetched, ...prev.filter((m) => m.id > newestFetchedId)]);
      // Auto-loading of older pages is (re-)armed once this scroll position has been applied.
      latestPendingRef.current = false;
      scrollModeRef.current = followBottom ? 'bottom' : 'arm';
      setError(null);
      if (document.visibilityState === 'visible') markChannelSeen(channelId, fetched[fetched.length - 1]?.id);
    } catch (err) {
      setError(err.message || 'Failed to load messages.');
      latestPendingRef.current = false;
      armAutoLoad();
    } finally {
      setLoading(false);
    }
  }, [channelId, markChannelSeen, armAutoLoad, updateHasMore, setOlderFailed]);

  useEffect(() => {
    setActiveChannelId(channelId);
    loadLatest();
    return () => {
      setActiveChannelId(null);
      clearTimeout(seenTimerRef.current);
      pageGenerationRef.current += 1;
      disarmAutoLoad();
    };
  }, [channelId, loadLatest, setActiveChannelId, disarmAutoLoad]);

  useEffect(() => subscribe((event) => {
    if (event.type === 'message' && event.message.channelId === channelId) {
      if (isNearBottom() || event.message.senderUserTenantId === userTenantId) {
        scrollModeRef.current = 'bottom';
      }
      setMessages((prev) => upsertMessage(prev, event.message));
      if (document.visibilityState === 'visible') scheduleSeen();
    } else if (event.type === 'deleted' && event.channelId === channelId) {
      setMessages((prev) => prev.map((m) => (m.id === event.messageId ? { ...m, ...DELETED_FIELDS } : m)));
    } else if (event.type === 'edited' && event.message.channelId === channelId) {
      // In-place update only: never scroll, mark seen, or treat as a new message.
      setMessages((prev) => applyMessageEdit(prev, event.message));
    } else if (event.type === 'reactions' && event.channelId === channelId) {
      setMessages((prev) => prev.map((m) => (m.id === event.messageId ? { ...m, reactions: event.reactions } : m)));
    } else if (event.type === 'resync') {
      // Older pages are dropped by the resync; ignore any in flight and pause auto-loading until the new page is shown.
      pageGenerationRef.current += 1;
      disarmAutoLoad();
      loadLatest({ resync: true });
    }
  }), [subscribe, channelId, userTenantId, scheduleSeen, loadLatest, disarmAutoLoad]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const mode = scrollModeRef.current;
    if (!el || !mode) return;
    if (mode === 'bottom') {
      el.scrollTop = el.scrollHeight;
    } else if (mode.preserve) {
      el.scrollTop = el.scrollHeight - mode.preserve.height + mode.preserve.top;
    }
    scrollModeRef.current = null;
    if (mode === 'bottom' || mode === 'arm') armAutoLoad();
  }, [messages, loading, armAutoLoad]);

  const loadOlder = useCallback(async () => {
    const list = messagesRef.current;
    if (!list.length || loadingOlderRef.current || !hasMoreRef.current) return;
    const generation = pageGenerationRef.current;
    loadingOlderRef.current = true;
    setLoadingOlder(true);
    setOlderFailed(false);
    try {
      const page = await unifiedApi.getChatMessages(channelId, { before: list[0].id });
      if (!page && generation === pageGenerationRef.current) {
        // 404: the member lost access (removed, or chat turned off). Stop paging instead of retrying forever.
        updateHasMore(false);
        setUnavailable(true);
      } else if (page && generation === pageGenerationRef.current) {
        const el = scrollRef.current;
        scrollModeRef.current = el ? { preserve: { height: el.scrollHeight, top: el.scrollTop } } : null;
        setMessages((prev) => [
          ...(page.messages || []).filter((m) => !prev.some((p) => p.id === m.id)),
          ...prev,
        ]);
        updateHasMore(page.hasMore);
      }
    } catch (err) {
      if (generation === pageGenerationRef.current) {
        setError(err.message || 'Failed to load earlier messages.');
        setOlderFailed(true);
      }
    } finally {
      loadingOlderRef.current = false;
      cooldownUntilRef.current = Date.now() + OLDER_LOAD_COOLDOWN_MS;
      setLoadingOlder(false);
      scheduleAutoLoadCheck(OLDER_LOAD_COOLDOWN_MS);
    }
  }, [channelId, scheduleAutoLoadCheck, updateHasMore, setOlderFailed]);

  /** Loads the previous page when the top sentinel is in view and nothing blocks it (see refs above). */
  const maybeAutoLoadOlder = useCallback(() => {
    clearTimeout(autoLoadTimerRef.current);
    if (!autoLoadArmedRef.current || !sentinelVisibleRef.current || !hasMoreRef.current
      || loadingOlderRef.current || olderLoadFailedRef.current) return;
    const wait = cooldownUntilRef.current - Date.now();
    if (wait > 0) {
      scheduleAutoLoadCheck(wait);
      return;
    }
    loadOlderRef.current?.();
  }, [scheduleAutoLoadCheck]);

  useEffect(() => {
    loadOlderRef.current = loadOlder;
    maybeAutoLoadRef.current = maybeAutoLoadOlder;
  }, [loadOlder, maybeAutoLoadOlder]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!supportsObserver || !root || !sentinel) return undefined;
    const observer = new window.IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) return;
      sentinelVisibleRef.current = entry.isIntersecting;
      if (entry.isIntersecting) maybeAutoLoadRef.current?.();
    }, { root, rootMargin: OLDER_ROOT_MARGIN });
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      sentinelVisibleRef.current = false;
    };
  }, [channelId, supportsObserver]);

  useEffect(() => () => clearTimeout(autoLoadTimerRef.current), []);

  const handleDragEnter = (event) => {
    if (!dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  };

  const handleDragOver = (event) => {
    if (!dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (event) => {
    if (!dragHasFiles(event.dataTransfer)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };

  const handleDrop = (event) => {
    if (!dragHasFiles(event.dataTransfer)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const file = pickPhotoFile(Array.from(event.dataTransfer.files || []));
    if (file) composerRef.current?.attachFile(file);
  };

  const appendSent = (message) => {
    scrollModeRef.current = 'bottom';
    setMessages((prev) => upsertMessage(prev, message));
  };

  // unifiedApi returns null for 404 (access revoked or channel disabled); treat that as a failed send.
  const deliver = async (send, fallbackError) => {
    setError(null);
    try {
      const message = await send();
      if (!message) {
        setError('This chat is no longer available, so your message wasn\'t sent.');
        return false;
      }
      appendSent(message);
      return true;
    } catch (err) {
      setError(err.message || fallbackError);
      return false;
    }
  };

  const sendText = (content) => deliver(() => unifiedApi.sendChatMessage(channelId, content), 'Failed to send message.');

  const sendPhoto = (file, caption) => deliver(() => unifiedApi.sendChatPhoto(channelId, file, caption), 'Failed to send photo.');

  const sendGif = (gif, caption) => deliver(() => unifiedApi.sendChatGif(channelId, gif.id, caption), 'Failed to send GIF.');

  const toggleReaction = async (message, emoji) => {
    const mine = (message.reactions || []).some((r) => r.emoji === emoji && r.userTenantIds?.includes(userTenantId));
    try {
      const reactions = mine
        ? await unifiedApi.removeChatReaction(message.id, emoji)
        : await unifiedApi.addChatReaction(message.id, emoji);
      if (Array.isArray(reactions)) {
        setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, reactions } : m)));
      }
    } catch (err) {
      setError(err.message || 'Failed to update reaction.');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await unifiedApi.deleteChatMessage(deleteTarget.id);
      setMessages((prev) => prev.map((m) => (m.id === deleteTarget.id ? { ...m, ...DELETED_FIELDS } : m)));
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || 'Failed to delete message.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  // Resolves true when saved; on failure shows the error and keeps the editor open.
  const editMessage = async (message, content) => {
    try {
      const updated = await unifiedApi.editChatMessage(message.id, content);
      if (!updated) {
        setError('This message can no longer be edited.');
        return false;
      }
      setMessages((prev) => applyMessageEdit(prev, updated));
      return true;
    } catch (err) {
      setError(err.message || 'Failed to edit message.');
      return false;
    }
  };

  const canAttach = !loading && !unavailable;

  // Membership announcements name the member as sender, but only moderators may remove them.
  const canDelete = (message) => (isSystemMessage(message)
    ? Boolean(channel.canModerate)
    : message.senderUserTenantId === userTenantId || channel.canModerate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, bgcolor: 'background.paper' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: { xs: 1, sm: 2 }, py: 1.25, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
      >
        {showBack && (
          <IconButton onClick={onBack} aria-label="Back to chats" edge="start">
            <BackIcon />
          </IconButton>
        )}
        <ChatChannelAvatar channel={channel} size={36} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {titleLink ? (
            <MuiLink
              component={RouterLink}
              to={titleLink}
              underline="hover"
              color="text.primary"
              variant="subtitle1"
              sx={{ fontWeight: 600, display: 'block' }}
              noWrap
            >
              {channel.name}
            </MuiLink>
          ) : (
            <Typography variant="subtitle1" fontWeight={600} noWrap>{channel.name}</Typography>
          )}
          {isLeague && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {channel.leagueName && channel.leagueName !== channel.name
                ? `${leagueWideCaption(channel)} · ${channel.leagueName}`
                : leagueWideCaption(channel)}
            </Typography>
          )}
          {isDirect && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>Direct message</Typography>
          )}
          {!isLeague && !isDirect && channel.leagueSlug && (
            <MuiLink
              component={RouterLink}
              to={`/leagues/${channel.leagueSlug}`}
              underline="hover"
              color="text.secondary"
              variant="caption"
              sx={{ display: 'block' }}
              noWrap
            >
              {channel.leagueName}
            </MuiLink>
          )}
          {!isLeague && !isDirect && !channel.leagueSlug && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{channel.leagueName}</Typography>
          )}
          {channel.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              title={channel.description}
              sx={{ display: 'block', fontStyle: 'italic' }}
              data-testid="chat-channel-description"
            >
              {channel.description}
            </Typography>
          )}
        </Box>
        {connectionState === 'reconnecting' && (
          <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0 }}>Reconnecting…</Typography>
        )}
        {!isDirect && <ChatChannelMembers channelId={channel.id} channelName={channel.name} currentUserTenantId={userTenantId} />}
        <Tooltip title={channel.isMuted ? 'Unmute notifications' : 'Mute notifications'}>
          <span>
            <IconButton
              onClick={toggleMute}
              disabled={muteBusy}
              aria-label={channel.isMuted ? `Unmute notifications for ${channel.name}` : `Mute notifications for ${channel.name}`}
              aria-pressed={Boolean(channel.isMuted)}
              edge={channel.canModerate || isDirect ? false : 'end'}
            >
              {channel.isMuted ? <NotificationsOffIcon /> : <NotificationsOnIcon />}
            </IconButton>
          </span>
        </Tooltip>
        {isDirect && (
          <Tooltip title="Delete conversation">
            <IconButton onClick={() => setHideOpen(true)} aria-label={`Delete conversation with ${channel.name}`} edge="end">
              <DeleteConversationIcon />
            </IconButton>
          </Tooltip>
        )}
        {channel.canModerate && (
          <Tooltip title="Channel settings">
            <IconButton onClick={() => setSettingsOpen(true)} aria-label="Channel settings" edge="end">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box
        data-testid="chat-drop-zone"
        onDragEnter={canAttach ? handleDragEnter : undefined}
        onDragOver={canAttach ? handleDragOver : undefined}
        onDragLeave={canAttach ? handleDragLeave : undefined}
        onDrop={canAttach ? handleDrop : undefined}
        sx={{
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative',
        }}
      >
        {dragActive && (
          <Box
            data-testid="chat-drop-overlay"
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 4,
              zIndex: 1,
              pointerEvents: 'none',
              border: 2,
              borderStyle: 'dashed',
              borderColor: 'primary.main',
              borderRadius: 2,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Typography variant="body2" color="primary" fontWeight={600} align="center">Drop a photo to attach it</Typography>
          </Box>
        )}
        <Box
          ref={scrollRef}
          data-testid="chat-message-list"
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1.5, sm: 2 }, py: 1 }}
        >
          <Box ref={sentinelRef} data-testid="chat-older-sentinel" aria-hidden="true" sx={{ height: '1px' }} />
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && unavailable && (
            <Alert severity="info" sx={{ mt: 2 }}>This chat is no longer available.</Alert>
          )}

          {!loading && !unavailable && (
            <>
              {loadingOlder && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <CircularProgress size={20} aria-label="Loading earlier messages" />
                </Box>
              )}

              {/* Always offered alongside automatic loading: keyboard and screen-reader users may never scroll the sentinel into view. */}
              {!loadingOlder && hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                  <Button size="small" onClick={loadOlder}>
                    {olderLoadFailed ? 'Retry loading earlier messages' : 'Load earlier messages'}
                  </Button>
                </Box>
              )}

              {!loadingOlder && !hasMore && messages.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  align="center"
                  component="p"
                  sx={{ py: 1 }}
                  data-testid="chat-start-of-conversation"
                >
                  Start of conversation
                </Typography>
              )}

              {messages.length === 0 && (
                <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                  {isDirect
                    ? `No messages yet. Say hello to ${channel.directMember?.firstName || channel.name}!`
                    : `No messages yet. Say hello to your ${channelAudience(channel)}!`}
                </Typography>
              )}

              {messages.map((message, index) => {
                const previous = messages[index - 1];
                const newDay = !previous || chatDayKey(previous.createdAt) !== chatDayKey(message.createdAt);
                return (
                  <React.Fragment key={message.id}>
                    {newDay && (
                      <Divider sx={{ my: 1.5 }}>
                        <Typography variant="caption" color="text.secondary">{formatChatDate(message.createdAt)}</Typography>
                      </Divider>
                    )}
                    <ChatMessageItem
                      message={message}
                      isOwn={message.senderUserTenantId === userTenantId}
                      showHeader={newDay || startsNewGroup(previous, message)}
                      canDelete={canDelete(message)}
                      onRequestDelete={setDeleteTarget}
                      canEdit={!isSystemMessage(message) && message.senderUserTenantId === userTenantId}
                      onEditMessage={editMessage}
                      onToggleReaction={toggleReaction}
                      currentUserTenantId={userTenantId}
                      onMessageSender={isDirect ? undefined : messageSender}
                    />
                  </React.Fragment>
                );
              })}
            </>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 0 }}>{error}</Alert>
        )}

        {!unavailable && (
          <ChatComposer
            ref={composerRef}
            channelName={channel.name}
            isDesktop={isDesktop}
            disabled={loading}
            onSendText={sendText}
            onSendPhoto={sendPhoto}
            onSendGif={sendGif}
            onError={setError}
            autoFocus={autoFocusComposer}
          />
        )}
      </Box>

      {channel.canModerate && (
        <ChatChannelSettingsDialog
          open={settingsOpen}
          channelId={channel.id}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => refreshChannels?.()}
        />
      )}

      {isDirect && (
        <Dialog open={hideOpen} onClose={() => !hiding && setHideOpen(false)}>
          <DialogTitle>Delete conversation?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`This removes your conversation with ${channel.name} from your chats. ${channel.name} won't be notified and keeps their copy. If either of you sends a new message, the conversation will show up again.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHideOpen(false)} disabled={hiding}>Cancel</Button>
            <Button onClick={confirmHide} color="error" variant="contained" disabled={hiding}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>{isSystemMessage(deleteTarget) ? 'Remove announcement?' : 'Delete message?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isSystemMessage(deleteTarget)
              ? 'This announcement will be removed for everyone in the chat.'
              : 'This message will be removed for everyone in the chat.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleting}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatConversation;
